/* eslint-disable no-labels */
class Matrix {
  constructor(src, width, height) {
    this.set(src, width, height)
  }

  get width() { return this.uint[0]*256 + this.uint[1] }
  get height() { return this.uint[2]*256 + this.uint[3] }
  get size() { return((this.uint[0]*256 + this.uint[1])*(this.uint[2]*256 + this.uint[3])) }
} // Matrix

Matrix.toBase62 = function(arr) {
  return String.fromCharCode(...arr.map(a=>a>61?122:a>=36?a+61:a>=10?a+55:a>=0?a+48:48))
}
Matrix.fromBase62 = function(str) {
  return Array.from(str).map(c=>c.charCodeAt(0)).map(c=>c>122?0:c>96?c-61:c>90?0:c>64?c-55:c>57?0:c>47?c-48:0)
}
Matrix.hashCompare = function(hash1, hash2) {
  const arr1 = Matrix.fromBase62(hash1)
  const arr2 = Matrix.fromBase62(hash2)
  const count = arr1.length>arr2.length ? arr1.length : arr2.length
  let sum = 0
  for(let i=0; i<count; i++) {
    const v1 = arr1[i] || 0
    const v2 = arr2[i] || 0
    const diff = Math.pow(v1-v2, 2)
    sum += diff
  }

  return Math.round(Math.sqrt(sum))
} // hashCompare

Matrix.hashSimilar = function(hash1, hash2) {
  const avg = Matrix.hashCompare(hash1, hash2)
  const probability = Math.round(avg<40 ? 100 : avg>60 ? 0 : (5*(60-avg)))
  return probability
} // hashSimilar

const m = Matrix.prototype

m.copyRef = function(src) {
  this.uint = src.uint
  this.data = this.uint.subarray(4)
  return this
} // copyRef

m.set = function(src, width, height) {
  if(typeof (src)==='number' && typeof (width)==='number') {
    height=width
    width=src
    src=null
  }
  if(src instanceof Matrix) {
    this.uint = src.uint.slice() // copy of other Matrix data
  }else if(src instanceof ArrayBuffer) {
    if(isNaN(width)) {
      this.uint = new Uint8Array(src).slice() // copy of ArrayBuffer
    }else{
      this.uint = new Uint8Array(src.byteLength+4)
      this.uint[0] = width>>8
      this.uint[1] = width&255
      this.uint[2] = height>>8
      this.uint[3] = height&255
      this.uint.set(src, 4)
    }
  }else if(src instanceof DataView) {
    if(isNaN(width)) {
      this.uint = new Uint8Array(src.buffer.slice(src.byteOffset, src.byteLength)) // copy of buffer in DataView
    }else{
      this.uint = new Uint8Array(src.byteLength+4)
      this.uint[0] = width>>8
      this.uint[1] = width&255
      this.uint[2] = height>>8
      this.uint[3] = height&255
      this.uint.set(src.buffer.slice(src.byteOffset, src.byteLength), 4)
    }
  }else if(ArrayBuffer.isView(src)) {
    if(isNaN(width)) {
      this.uint = new Uint8Array(src) // copy of typed array
    }else{
      this.uint = new Uint8Array(src.byteLength+4)
      this.uint[0] = width>>8
      this.uint[1] = width&255
      this.uint[2] = height>>8
      this.uint[3] = height&255
      this.uint.set(src, 4)
    }
  }else{
    width = width>0 ? width : 0
    height = height>0 ? height : 0
    const pixels = width*height
    this.uint = new Uint8Array(pixels+4)
    this.uint[0] = width>>8
    this.uint[1] = width&255
    this.uint[2] = height>>8
    this.uint[3] = height&255
    if(Array.isArray(src)) {
      for(let i=0; i<pixels; i++) {
        this.uint[i+4] = src[i]<0 ? 0 : src[i]>255 ? 255 : src[i]>=0 ? src[i] : 0
      }
    }
  }
  this.data = this.uint.subarray(4)
} // set

m.extractMe = function(...args) { args[4]=1; return this.extract(...args) }
m.extract = function(x, y, width, height, inplace) {
  const src = this.data
  const src_width = this.width
  const src_height = this.height

  if(!(x>=0)) x=0
  if(!(y>=0)) y=0
  if(x>src_width) x = src_width
  if(y>src_height) y = src_height
  if(width<=0) width = src_width+width
  if(height<=0) height = src_height+height
  if(!(width>=0)) width=0
  if(!(height>=0)) height=0
  if(x+width>src_width) width = src_width-x
  if(y+height>src_height) height = src_height-y

  const dst_matrix = new Matrix(width, height)
  const dst = dst_matrix.data
  for(let dy=0; dy<height; dy++) {
    const offset = (y+dy)*src_width+x
    dst.set(src.subarray(offset, offset+width), dy*width)
  }
  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // extract

m.padMe = function(...args) { args[5]=1; return this.pad(...args) }
m.pad = function(value, top, bottom, left, right, inplace) {
  const src = this.data
  const src_width = this.width
  const src_height = this.height

  if(!(top>=0)) top=0
  if(!(bottom>=0)) bottom=0
  if(!(left>=0)) left=0
  if(!(right>=0)) right=0
  value = value>255 ? 255 : value>0 ? value : 0
  const dst_width = src_width+left+right
  const dst_height = src_height+top+bottom

  const dst_matrix = new Matrix(dst_width, dst_height)
  const dst = dst_matrix.data
  dst.fill(value)
  for(let dy=0; dy<src_height; dy++) {
    const src_offset = dy*src_width
    const dst_offset = (dy+top)*dst_width+left
    dst.set(src.subarray(src_offset, src_offset+src_width), dst_offset)
  }
  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // pad

m.borderMe = function(...args) { args[5]=1; return this.border(...args) }
m.border = function(value, top, bottom, left, right, inplace) {
  value = value>0 ? value : 0
  const dst_matrix = inplace ? this : new Matrix(this)
  const dst = dst_matrix.data
  const width = dst_matrix.width
  const height = dst_matrix.height
  const size = dst_matrix.size
  dst.fill(value, 0, top*width)
  dst.fill(value, (height-bottom)*width, size)
  for(let y=top; y<height-bottom; y++) {
    dst.fill(value, y*width, y*width+left)
    dst.fill(value, (y+1)*width-right, (y+1)*width)
  }
  return dst_matrix
} // border

m.cropBordersMe = function(...args) { args[3]=1; return this.cropBorders(...args) }
m.cropBorders = function(threshold, h_max, v_max, inplace) {
  const src = this.data
  const width = this.width
  const height = this.height

  let top=0; let bottom=0; let left=0; let right=0
  a: for(let y=0; y<height; y++) {
    let max=0
    for(let x=0; x<width; x++) {
      if(src[y*width+x]<threshold) {
        max++
        if(max>h_max) break a
      }
    }
    top++
  }
  a: for(let y=height-1; y>=0; y--) {
    let max=0
    for(let x=0; x<width; x++) {
      if(src[y*width+x]<threshold) {
        max++
        if(max>h_max) break a
      }
    }
    bottom++
  }
  a: for(let x=0; x<width; x++) {
    let max=0
    for(let y=top; y<height-bottom; y++) {
      if(src[y*width+x]<threshold) {
        max++
        if(max>v_max) break a
      }
    }
    left++
  }
  a: for(let x=width-1; x>=0; x--) {
    let max=0
    for(let y=top; y<height-bottom; y++) {
      if(src[y*width+x]<threshold) {
        max++
        if(max>v_max) break a
      }
    }
    right++
  }

  const dst_matrix = this.extract(left, top, width-left-right, height-top-bottom)
  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // cropBorders

m.shrinkMe = function(...args) { args[2]=1; return this.shrink(...args) }
m.shrink = function(dst_width, dst_height, inplace) {
  const src = this.data
  const src_width = this.width
  const src_height = this.height

  const dst_matrix = new Matrix(dst_width, dst_height)
  const dst = dst_matrix.data
  let x, y, i, ii
  let gray
  let xfrag, yfrag, xfrag2, yfrag2
  let xt, yt
  let xi, yi

  const dx = src_width/dst_width
  const dy = src_height/dst_height

  for(yt= 0, y=0; y<dst_height; y++, yt += dy) {
    yfrag = Math.ceil(yt) - yt
    if(yfrag === 0) yfrag = 1
    yfrag2 = yt+dy - Math.floor(yt + dy)
    if(yfrag2 === 0 && dy !== 1.0) yfrag2 = 1

    for(xt = 0, x=0; x<dst_width; x++, xt+= dx) {
      xi = Math.floor(xt)
      yi = Math.floor(yt)
      xfrag = Math.ceil(xt) - xt
      if(xfrag === 0) xfrag = 1
      xfrag2 = xt+dx - Math.floor(xt+dx)
      if(xfrag2 === 0 && dx !== 1.0) xfrag2 = 1
      gray = xfrag * yfrag * src[yi*src_width+xi]

      for(i=0; xi + i + 1 < xt+dx-1; i++) gray += yfrag * src[yi*src_width+xi+i+1]
      gray += xfrag2 * yfrag * src[yi*src_width+xi+i+1]
      for(i=0; yi+i+1 < yt +dy-1 && yi + i+1 < src_height; i++) {
        gray += xfrag * src[(yi+i+1)*src_width+xi]
        for(ii = 0; xi + ii + 1 < xt + dx - 1 && xi + ii + 1 < src_width; ii++) gray += src[(yi+i+1)*src_width+xi+ii+1]
        if(yi + i + 1 < src_height && xi + ii + 1 < src_width) gray += xfrag2 * src[(yi+i+1)*src_width+xi+ii+1]
      }

      if(yi + i + 1 < src_height) {
        gray += xfrag * yfrag2 * src[(yi + i + 1)*src_width + xi]
        for(ii = 0; xi + ii + 1 < xt + dx - 1 && xi + ii + 1 < src_width; ii++) gray += yfrag2 * src[(yi + i + 1)*src_width + xi + ii + 1]
      }

      if (yi + i + 1 < src_height && xi + ii + 1 < src_width) gray += xfrag2 * yfrag2 * src[(yi + i + 1)*src_width + xi + ii + 1]

      gray /= dx * dy
      gray = Math.round(gray)
      gray = gray < 0 ? 0 : gray>255 ? 255 : gray
      dst[y*dst_width+x] = gray
    }
  }
  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // resize

m.gauss9Me = function(...args) { args[0]=1; return this.gauss9(...args) }
m.gauss9 = function(inplace) {
  const src = this.data
  const width = this.width
  const height = this.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1
  const dst_matrix = new Matrix(width, height)
  const dst = dst_matrix.data
  dst.fill(0)

  // at the image border just copy pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = src[x]
    dst[last_line_offset+x] = src[last_line_offset+x]
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = src[first_col_start]
    dst[first_col_start+width1] = src[first_col_start+width1]
    first_col_start += width
  }

  // calculate 3x3 Gauss for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      const sum =
        src[offset-width2] + 2*src[offset-width] + src[offset-width1] +
        2*src[offset-1] + 4*src[offset] + 2*src[offset+1] +
        src[offset+width1] + 2*src[offset+width] + src[offset+width2]
      dst[offset] = Math.floor(sum/16)
      offset++
    }
    offset+=2
  }

  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // gauss9

m.binaryMe = function(...args) { args[1]=1; return this.binary(...args) }
m.binary = function(threshold, inplace) {
  threshold = threshold>0 ? threshold : 128
  const src = this.data
  const size = this.size
  const dst_matrix = inplace ? this : new Matrix(this)
  const dst = dst_matrix.data
  for(let i=0; i<size; i++) {
    dst[i] = src[i]>=threshold ? 255 : 0
  }
  return dst_matrix
} // binary

m.sobelMe = function(...args) { args[1]=1; return this.sobel(...args) }
m.sobel = function(threshold, inplace) {
  threshold = threshold>0 ? threshold : 0
  const threshold2 = threshold*threshold // make it faster to limit SQRT operations
  const src = this.data
  const width = this.width
  const height = this.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1
  const dst_matrix = new Matrix(width, height)
  const dst = dst_matrix.data
  dst.fill(0)

  // at the image border just empty pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = 0
    dst[last_line_offset+x] = 0
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = 0
    dst[first_col_start+width1] = 0
    first_col_start += width
  }

  // calculate 3x3 Sobel for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      const gx = (
        -src[offset-width2] + src[offset-width1] -
        2*src[offset-1] + 2*src[offset+1] -
        src[offset+width1] + src[offset+width2]
      )/4
      const gy = (
        +src[offset-width2] + 2*src[offset-width] + src[offset-width1] -
        src[offset+width1] - 2*src[offset+width] - src[offset+width2]
      )/4
      const g2 = gx*gx+gy*gy
      dst[offset] = threshold2 ? (g2>=threshold2 ? 255 : 0) : Math.round(Math.sqrt(g2))
      offset++
    }
    offset+=2
  }

  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // sobel

m.getBlockAverage = function(x1, y1, x2, y2) {
  const src = this.data
  const width = this.width
  const height = this.height

  if(x1<0) x1=0
  if(x2>width-1) x2=width-1
  if(y1<0) y1=0
  if(y2>height-1) y2=height-1

  const count=(x2-x1+1)*(y2-y1+1)
  let sum=0
  for(let y=y1; y<=y2; y++) {
    for(let x=x1; x<=x2; x++) sum += src[y*width+x]
  }
  return Math.round(sum / count)
} // getBlockAverage

m.getBlocksAverage = function(rows, cols, enlarge_h, enlarge_v) {
  const width = this.width
  const height = this.height

  const averages = []
  let y1 = 0
  for(let row=0; row<rows; row++) {
    const y2 = Math.round(height/rows*(row+1))
    let x1 = 0
    for(let col=0; col<cols; col++) {
      const x2 = Math.round(width/cols*(col+1))
      averages.push(this.getBlockAverage(x1-enlarge_h, y1-enlarge_v, x2-1+enlarge_h, y2-1+enlarge_v))
      x1 = x2
    }
    y1 = y2
  }

  return averages
} // getBlocksAverage

m.avgBlocksMe = function(...args) { args[4]=1; return this.avgBlocks(...args) }
m.avgBlocks = function(rows, cols, enlarge_h, enlarge_v, inplace) {
  const width = this.width
  const height = this.height
  const dst_matrix = new Matrix(width, height)
  const dst = dst_matrix.data
  dst.fill(0)

  let y1 = 0
  for(let row=0; row<rows; row++) {
    const y2 = Math.round(height/rows*(row+1))
    let x1 = 0
    for(let col=0; col<cols; col++) {
      const x2 = Math.round(width/cols*(col+1))
      const avg = this.getBlockAverage(x1-enlarge_h, y1-enlarge_v, x2-1+enlarge_h, y2-1+enlarge_v)
      for(let y=y1; y<y2; y++) {
        for(let x=x1; x<x2; x++) dst[y*width+x] = avg
      }
      x1 = x2
    }
    y1 = y2
  }

  if(inplace) return this.copyRef(dst_matrix)
  return dst_matrix
} // avgBlocks

// construct Uint8Array containing RGBA data formed from our grayacale data
m.rgba = function() {
  const size = this.size
  const rgba = new Uint8ClampedArray(size*4)
  for(let i=0; i<size; i++) {
    rgba[i*4+0] = rgba[i*4+1] = rgba[i*4+2] = this.data[i]
    rgba[i*4+3] = 255
  }
  return rgba
} // rgba

m.imageData = function() {
  return new ImageData(this.rgba(), this.width, this.height)
} // imageData

m.P5 = function() {
  const header = `P5 ${this.width} ${this.height} ${255}\n`
  const header_uint = Uint8Array.from([...header].map(c=>c.charCodeAt()))
  const out = new Uint8Array(header_uint.length+this.size)
  out.set(header_uint, 0)
  out.set(this.data, header_uint.length)
  return out
} // P5

m.hash = function(rows, cols, enlarge_h, enlarge_v) {
  // calculate visual image hash
  cols = cols || 8
  rows = rows || 4
  enlarge_h = enlarge_h || 1
  enlarge_v = enlarge_v || 1
  return Matrix.toBase62(this.getBlocksAverage(rows, cols, enlarge_h, enlarge_v).map(x=>255-Math.round(x)))
} // hash

m.compareSSIM = function(src) {
  // Mean Structural Similarity Index
  const size = this.size
  if(src.size!==size) return
  return 123
} // compareSSIM

m.compareMSE = function(m2_matrix, kernel, move) {
  // Mean squared error
  kernel = kernel>=0 ? parseInt(kernel) : 1
  move = move>=0 ? parseInt(move) : kernel
  const width = this.width
  const height = this.height
  const size = this.size
  const m1 = this.data
  const m2 = m2_matrix.data
  if(m2_matrix.size!==size) return
  let sum = 0
  let count = 0
  for(let y1=kernel; y1<height-kernel; y1+=move) {
    for(let x1=kernel; x1<width-kernel; x1+=move) {
      let e1 = 0
      let e2 = 0
      for(let y2=-kernel; y2<=kernel; y2++) {
        for(let x2=-kernel; x2<=kernel; x2++) {
          const offset = (y1+y2)*width + (x1+x2)
          e1 += m1[offset]
          e2 += m2[offset]
        }
      }
      const k_pixels = (2*kernel+1)*(2*kernel+1)
      e1 /= k_pixels
      e2 /= k_pixels
      sum += Math.pow(e1-e2, 2)
      count++
    }
  }
  sum /= count
  return Math.round(sum)
} // compareMSE

module.exports = Matrix
